import {
  Entity,
  parseEntityRef,
  ResourceEntity,
} from '@backstage/catalog-model';
import { JsonObject } from '@backstage/types';

/** Frontend plugin id for the Tessa environments plugin. */
export const TESSA_ENVIRONMENTS_PLUGIN_ID = 'ns-tessa-environments';

/** Page names registered by the Tessa environments plugin. */
export const TESSA_ENVIRONMENTS_INDEX_PAGE_NAME = 'index';
export const TESSA_ENVIRONMENTS_DETAIL_PAGE_NAME = 'detail';

/** Full extension ids used to govern the Tessa environment pages. */
export const TESSA_ENVIRONMENTS_INDEX_EXTENSION_ID = `page:${TESSA_ENVIRONMENTS_PLUGIN_ID}/${TESSA_ENVIRONMENTS_INDEX_PAGE_NAME}`;
export const TESSA_ENVIRONMENTS_DETAIL_EXTENSION_ID = `page:${TESSA_ENVIRONMENTS_PLUGIN_ID}/${TESSA_ENVIRONMENTS_DETAIL_PAGE_NAME}`;

/** API version for NS custom entities. */
export const NS_API_VERSION = 'ns.nl/v1alpha1';

export function isSupportedNsApiVersion(apiVersion: string): boolean {
  return apiVersion === NS_API_VERSION;
}

/** Annotation prefix used on Tessa entities (e.g. `tessa.ns.nl/environment-key`). */
export const TESSA_ANNOTATION_PREFIX = 'tessa.ns.nl';

/**
 * Annotation suffix carrying the number of team members in a Tessa
 * environment, pre-computed by the environments provider so the environments
 * list page never has to read a roster to count it.
 * Combine with {@link TESSA_ANNOTATION_PREFIX} for the full annotation key.
 */
export const TESSA_MEMBER_COUNT_ANNOTATION = 'member-count';

/** Resource type for environments provided by Tessa. */
export const TESSA_ENVIRONMENT_RESOURCE_TYPE = 'tessa-environment';

/** Resource type for license types provided by Tessa. */
export const TESSA_LICENSE_TYPE_RESOURCE_TYPE = 'tessa-license-type';

/** Whether `value` is a plain object (not an array, not `null`). */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
}

export function isSafeHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

function isParsableUserRef(value: string): boolean {
  try {
    parseEntityRef(value, {
      defaultKind: 'User',
      defaultNamespace: 'default',
    });
    return true;
  } catch {
    return false;
  }
}

/** Whether `value` is a string with non-whitespace content. */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

// ---------------------------------------------------------------------------
// Tessa environment Resource
// ---------------------------------------------------------------------------

/** A Resource entity representing an environment provided by Tessa. */
export interface TessaEnvironmentResourceEntity extends ResourceEntity {
  apiVersion: 'backstage.io/v1alpha1';
  kind: 'Resource';
  metadata: ResourceEntity['metadata'] & {
    annotations: Record<string, string>;
    name: string;
    title: string;
  };
  spec: TessaEnvironmentSpec;
}

/**
 * Spec of a Tessa environment Resource.
 *
 * The built-in `Resource` kind schema accepts additional `spec` properties at
 * runtime (verified against `resourceEntityV1alpha1Validator`); only the
 * TypeScript type is closed, which this widening reopens.
 */
export type TessaEnvironmentSpec = ResourceEntity['spec'] & {
  /** Product component to URL, replacing one entity per component. */
  integrations?: Record<string, string>;
  /** The environment's roster. */
  members?: TessaEnvironmentMember[];
  type: typeof TESSA_ENVIRONMENT_RESOURCE_TYPE;
};

/**
 * One member of a Tessa environment, carried in the environment Resource's
 * `spec.members`.
 *
 * The roster lives in `spec` rather than an annotation deliberately: a
 * `fields` projection can select individual `spec` properties, so the
 * environments list page can exclude the roster entirely, while
 * `metadata.annotations` is all-or-nothing and would drag every roster onto
 * that page.
 */
export interface TessaEnvironmentMember extends JsonObject {
  /**
   * Human-readable name, copied from the matching `User` when one could be
   * resolved. Absent otherwise, in which case the principal name is shown.
   */
  displayName?: string;
  /** The member's occupation, as reported by Tessa. */
  occupation: string;
  /** Roles the member holds in this environment. */
  roles: string[];
  /** The user principal name (e.g. john.doe@ns.nl). */
  userPrincipalName: string;
  /**
   * Entity ref of the matching `User`, when one could be resolved. Absent when
   * no catalog User carries this principal name — the member is still listed,
   * but no relation is emitted for them.
   */
  userRef?: string;
}

/** Reads the roster off a Tessa environment Resource. */
export function getEnvironmentMembers(
  entity: Entity,
): TessaEnvironmentMember[] {
  const members = (entity.spec as { members?: unknown } | undefined)?.members;
  if (!Array.isArray(members)) {
    return [];
  }

  return members.filter(
    (member): member is TessaEnvironmentMember =>
      isRecord(member) &&
      typeof member.occupation === 'string' &&
      typeof member.userPrincipalName === 'string' &&
      isStringArray(member.roles) &&
      // userRef is optional but must be parseable as a catalog entity ref when
      // present. The consumer (EnvironmentMembershipProcessor) passes it
      // directly to parseEntityRef; rejecting malformed strings here prevents
      // processor-time throws (F4/F5 fix).
      (member.userRef === undefined ||
        (typeof member.userRef === 'string' &&
          isParsableUserRef(member.userRef))) &&
      // displayName is optional but must be a string when present (F4 fix).
      (member.displayName === undefined ||
        typeof member.displayName === 'string'),
  );
}

/**
 * Reads the roster off a Tessa environment Resource, preserving the
 * distinction between "roster unknown" and "roster known and empty".
 *
 * Returns `undefined` when `spec.members` is absent — the backend sync has
 * not yet completed, so "unknown" and "empty" must not be conflated.
 * Returns `[]` when the roster is known to be empty.
 * Returns the validated member list otherwise.
 *
 * Use this in preference to `getEnvironmentMembers` wherever the caller needs
 * to surface the "not yet loaded" state to the user.
 */
export function readEnvironmentMembers(
  entity: Entity,
): TessaEnvironmentMember[] | undefined {
  const spec = entity.spec as { members?: unknown } | undefined;
  if (!Array.isArray(spec?.members)) return undefined;
  return getEnvironmentMembers(entity);
}

/**
 * Reads the product-component integrations off a Tessa environment Resource,
 * as a map of product component to URL.
 */
export function getEnvironmentIntegrations(
  entity: Entity,
): Record<string, string> {
  const integrations = (entity.spec as { integrations?: unknown } | undefined)
    ?.integrations;
  if (!isRecord(integrations)) {
    return {};
  }

  const result: Record<string, string> = {};
  for (const [productComponent, url] of Object.entries(integrations)) {
    if (typeof url === 'string' && isSafeHttpUrl(url)) {
      result[productComponent] = url;
    }
  }
  return result;
}

/** Type guard for Resource entities produced by the Tessa environments provider. */
export function isTessaEnvironmentResourceEntity(
  entity: Entity,
): entity is TessaEnvironmentResourceEntity {
  if (
    entity.apiVersion !== 'backstage.io/v1alpha1' ||
    entity.kind !== 'Resource' ||
    !isRecord(entity.spec) ||
    entity.spec.type !== TESSA_ENVIRONMENT_RESOURCE_TYPE ||
    !isRecord(entity.metadata)
  ) {
    return false;
  }

  return (
    isNonEmptyString(entity.metadata.name) &&
    isNonEmptyString(entity.metadata.title) &&
    typeof getTessaAnnotation(entity, 'environment-key') === 'string'
  );
}

// ---------------------------------------------------------------------------
// Shared utilities
// ---------------------------------------------------------------------------

/**
 * Reads a Tessa annotation value from an entity's metadata.
 *
 * @example getTessaAnnotation(entity, 'environment-key')
 *          // reads entity.metadata.annotations['tessa.ns.nl/environment-key']
 */
export function getTessaAnnotation(
  entity: Entity,
  key: string,
): string | undefined {
  return entity.metadata.annotations?.[`${TESSA_ANNOTATION_PREFIX}/${key}`];
}

// ---------------------------------------------------------------------------
// Tessa license type Resource
// ---------------------------------------------------------------------------

/** A Resource entity representing a license type provided by Tessa. */
export interface TessaLicenseTypeResourceEntity extends ResourceEntity {
  apiVersion: 'backstage.io/v1alpha1';
  kind: 'Resource';
  metadata: ResourceEntity['metadata'] & {
    annotations: Record<string, string>;
    name: string;
    title: string;
  };
  spec: ResourceEntity['spec'] & {
    type: typeof TESSA_LICENSE_TYPE_RESOURCE_TYPE;
  };
}

/** Type guard for Resource entities produced by the Tessa license-types provider. */
export function isTessaLicenseTypeResourceEntity(
  entity: Entity,
): entity is TessaLicenseTypeResourceEntity {
  if (
    entity.apiVersion !== 'backstage.io/v1alpha1' ||
    entity.kind !== 'Resource' ||
    !isRecord(entity.spec) ||
    entity.spec.type !== TESSA_LICENSE_TYPE_RESOURCE_TYPE ||
    !isRecord(entity.metadata)
  ) {
    return false;
  }

  return (
    isNonEmptyString(entity.metadata.name) &&
    isNonEmptyString(entity.metadata.title)
  );
}

/**
 * Reads the pre-computed member count off a Tessa environment Resource.
 *
 * Returns `undefined` when the annotation is absent or unparsable, which is
 * deliberately distinct from `0`: the provider omits the annotation entirely
 * when the membership sync could not supply a count, so the UI can render
 * "unknown" instead of claiming an environment has no members.
 */
export function getEnvironmentMemberCount(entity: Entity): number | undefined {
  const raw = getTessaAnnotation(entity, TESSA_MEMBER_COUNT_ANNOTATION);
  if (raw === undefined) {
    return undefined;
  }

  const parsed = Number.parseInt(raw, 10);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
}

/**
 * Slugifies a string to a valid Backstage entity name.
 * Lowercases, replaces non-alphanumeric runs with a single hyphen,
 * and trims leading/trailing hyphens.
 */
export function slugifyEntityName(value: string): string {
  return value
    .toLowerCase()
    .split(/[^\da-z]+/)
    .filter(Boolean)
    .join('-');
}
